import { z } from "zod";

/**
 * Combined events — the men's decathlon and the women's heptathlon.
 *
 * One event, one phase whose table is POINTS_SUM, one meet per discipline
 * (`round_no` 1…N in the order below). Each discipline meet keeps its own
 * family (the 100 m is TIMED, the long jump ATTEMPTS) and carries
 * `config.combined = { kind, discipline }`, so its points are computed from
 * its own result — never found by matching an event name, which is how the
 * predecessor scored a men's long jump on the women's table.
 *
 * Points are the World Athletics scoring formula, computed on every keystroke
 * by the grids and saved as the slot's `points`; the phase's standings add
 * them up. The formula lives HERE only — the predecessor kept a client and a
 * server copy, kept in sync by hand.
 *
 * Source: World Athletics Scoring Tables for Combined Events (2017 edition,
 * formulas and coefficients unchanged since 1985). Confirm with the
 * technical delegate.
 */

export const COMBINED_KINDS = ["DECATHLON", "HEPTATHLON"] as const;
export type CombinedKind = (typeof COMBINED_KINDS)[number];

/** Every discipline either combined event uses. */
export const COMBINED_DISCIPLINES = ["100", "200", "400", "800", "1500", "100H", "110H", "HJ", "PV", "LJ", "SP", "DT", "JT"] as const;
export type CombinedDiscipline = (typeof COMBINED_DISCIPLINES)[number];

/** The competition order — round 1 is the first. The running total follows it. */
export const COMBINED_ORDER = {
	DECATHLON: ["100", "LJ", "SP", "HJ", "400", "110H", "DT", "PV", "JT", "1500"],
	HEPTATHLON: ["100H", "HJ", "SP", "200", "LJ", "JT", "800"],
} as const satisfies Record<CombinedKind, readonly CombinedDiscipline[]>;

/** Day 1 is the first N disciplines (decathlon 5 + 5, heptathlon 4 + 3). */
export const COMBINED_DAY_ONE = { DECATHLON: 5, HEPTATHLON: 4 } as const satisfies Record<CombinedKind, number>;

/** A field discipline of a combined event has three trials and no cut (TR 39.8). */
export const COMBINED_FIELD_ATTEMPTS = 3;

/**
 *   track   points = A × (B − T)^C, T in seconds
 *   field   points = A × (M − B)^C, M in centimetres (jumps) or metres (throws)
 */
interface Coefficients {
	a: number;
	b: number;
	c: number;
	kind: "track" | "jump" | "throw";
}

/**
 * The tables are per combined event: the high jump, long jump, shot put and
 * javelin are in both with DIFFERENT coefficients (a 6.63 m long jump is 727
 * decathlon points, 1049 heptathlon points).
 */
const TABLES: Record<CombinedKind, Partial<Record<CombinedDiscipline, Coefficients>>> = {
	DECATHLON: {
		"100": { a: 25.4347, b: 18, c: 1.81, kind: "track" },
		LJ: { a: 0.14354, b: 220, c: 1.4, kind: "jump" },
		SP: { a: 51.39, b: 1.5, c: 1.05, kind: "throw" },
		HJ: { a: 0.8465, b: 75, c: 1.42, kind: "jump" },
		"400": { a: 1.53775, b: 82, c: 1.81, kind: "track" },
		"110H": { a: 5.74352, b: 28.5, c: 1.92, kind: "track" },
		DT: { a: 12.91, b: 4, c: 1.1, kind: "throw" },
		PV: { a: 0.2797, b: 100, c: 1.35, kind: "jump" },
		JT: { a: 10.14, b: 7, c: 1.08, kind: "throw" },
		"1500": { a: 0.03768, b: 480, c: 1.85, kind: "track" },
	},
	HEPTATHLON: {
		"100H": { a: 9.23076, b: 26.7, c: 1.835, kind: "track" },
		HJ: { a: 1.84523, b: 75, c: 1.348, kind: "jump" },
		SP: { a: 56.0211, b: 1.5, c: 1.05, kind: "throw" },
		"200": { a: 4.99087, b: 42.5, c: 1.81, kind: "track" },
		LJ: { a: 0.188807, b: 210, c: 1.41, kind: "jump" },
		JT: { a: 15.9803, b: 3.8, c: 1.04, kind: "throw" },
		"800": { a: 0.11193, b: 254, c: 1.88, kind: "track" },
	},
};

/**
 * What a hand-held stopwatch time gets added before it is scored: 0.24 s up
 * to 200 m and the hurdles, 0.14 s for the 400 m; none for longer races.
 */
const HAND_TIMING_MS: Partial<Record<CombinedDiscipline, number>> = { "100": 240, "200": 240, "100H": 240, "110H": 240, "400": 140 };

/** The discipline is a race (TIMED), else a jump or throw (ATTEMPTS). */
export function isCombinedTrack(discipline: CombinedDiscipline): boolean {
	return ["100", "200", "400", "800", "1500", "100H", "110H"].includes(discipline);
}

/** A vertical jump (a heights card). */
export function isCombinedHeights(discipline: CombinedDiscipline): boolean {
	return discipline === "HJ" || discipline === "PV";
}

/** The discipline belongs to the combined event. */
export function inCombined(kind: CombinedKind, discipline: CombinedDiscipline): boolean {
	return (COMBINED_ORDER[kind] as readonly CombinedDiscipline[]).includes(discipline);
}

/**
 * The points one performance scores, a whole number (rounded down), 0 for a
 * performance at or beyond the table's zero (B) or for no performance.
 *
 *   value      the slot's `result_value` in the headline unit: milliseconds
 *              for a race, millimetres for a jump or throw
 *   handTimed  the race was timed by hand (+0.24 / +0.14 s first)
 *
 * A fully automatic time is scored to the hundredth, a time in thousandths
 * rounded UP (TR 19.24: 10.111 → 10.12). Jumps are scored in whole
 * centimetres, throws in metres to the centimetre (both rounded down).
 */
export function combinedPoints(kind: CombinedKind, discipline: CombinedDiscipline, value: number | null, opts: { handTimed?: boolean } = {}): number {
	const t = TABLES[kind][discipline];
	if (!t || value === null || !Number.isFinite(value) || value <= 0) return 0;
	let base: number;
	if (t.kind === "track") {
		const ms = value + (opts.handTimed ? (HAND_TIMING_MS[discipline] ?? 0) : 0);
		const seconds = Math.ceil(ms / 10) / 100;
		base = t.b - seconds;
	} else {
		const cm = Math.floor(value / 10);
		base = (t.kind === "jump" ? cm : cm / 100) - t.b;
	}
	if (base <= 0) return 0;
	return Math.floor(t.a * base ** t.c);
}

/**
 * A meet's place in a combined event — `config.combined` on a TIMED or
 * ATTEMPTS meet. The discipline must belong to the kind.
 */
export const combinedMeetConfig = z
	.object({
		kind: z.enum(COMBINED_KINDS),
		discipline: z.enum(COMBINED_DISCIPLINES),
		/** The race was timed by hand: its points add the hand-timing correction. */
		hand_timed: z.boolean().optional(),
	})
	.strict()
	.refine((c) => inCombined(c.kind, c.discipline), { message: "This discipline is not part of that combined event.", path: ["discipline"] });
export type CombinedMeetConfig = z.infer<typeof combinedMeetConfig>;

/** A meet config's `combined`, read defensively (the config came over the wire). null = not a combined-event discipline. */
export function readCombined(config: Record<string, unknown> | null | undefined): CombinedMeetConfig | null {
	const parsed = combinedMeetConfig.safeParse(config?.combined);
	return parsed.success ? parsed.data : null;
}

/**
 * The tie-break between two athletes level on total points, as a comparator
 * (negative = `a` ahead, 0 = still tied).
 *
 *   1. the athlete who scored more points than the other in MORE disciplines
 *   2. then the higher score in any one discipline
 *
 * `points` are per discipline, in competition order; a discipline not
 * contested is 0. Source: World Athletics Technical Rule 39.12.
 */
export function combinedTieBreak(a: readonly number[], b: readonly number[]): number {
	let aWins = 0;
	let bWins = 0;
	for (let i = 0; i < Math.max(a.length, b.length); i++) {
		const pa = a[i] ?? 0;
		const pb = b[i] ?? 0;
		if (pa > pb) aWins++;
		else if (pb > pa) bWins++;
	}
	if (aWins !== bWins) return bWins - aWins;
	return Math.max(0, ...b) - Math.max(0, ...a);
}
